import { PlusCircleOutlined } from '@ant-design/icons'
import { useRateManagement } from '@api/useFreight/useFreight'
import type { ManualAccessorial, Ratebook } from '@api/useFreight/types'
import { BBDTag, GraviButton, GraviGrid, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, ICellRendererParams, IFilter, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { Drawer, Form, message, Tooltip } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'

import { SingleProductGroupCellRenderer } from '../../../../shared/LineItems/components'
import { CreateAccessorialForm } from './CreateAccessorialForm'
import { RateHistoryDrawer } from './RateHistoryDrawer'
import {
  ActiveRenderer,
  ApprovalRenderer,
  EffectiveDateRenderer,
  ExcludeFromInvoiceRenderer,
  FeeTypeRenderer,
  RateRenderer,
  ReasonCodeRenderer,
} from './renderers'
import { NextRateRenderer } from '../AutomaticAccessorials/renderers'

export function ManualAccessorialsTab({ ratebook }: { ratebook: Ratebook }) {
  const {
    useManualAccessorialsQuery,
    useCreateManualAccessorial,
    useCreateManualAccessorialRate,
    useDisableManualAccessorial,
  } = useRateManagement()

  const [createDrawerVisible, setCreateDrawerVisible] = React.useState(false)
  const [rateHistoryDrawerVisible, setRateHistoryDrawerVisible] = React.useState(false)
  const [selectedAccessorialId, setSelectedAccessorialId] = React.useState<string | null>(null)
  const [editingAccessorial, setEditingAccessorial] = React.useState<ManualAccessorial | null>(null)

  // Fetch accessorial data
  const {
    data: records = [],
    isLoading: isLoadingAccessorials,
    refetch: refetchAccessorials,
  } = useManualAccessorialsQuery(ratebook)

  // Get the current selected accessorial from the records
  const selectedAccessorial = React.useMemo(() => {
    if (!selectedAccessorialId || !records?.length) return null
    return records.find((acc) => acc._id === selectedAccessorialId || acc.id === selectedAccessorialId) || null
  }, [records, selectedAccessorialId])

  const createAccessorialMutation = useCreateManualAccessorial()
  const createRateMutation = useCreateManualAccessorialRate()
  const disableAccessorialMutation = useDisableManualAccessorial()

  // Handle drawer visibility
  const showCreateDrawer = () => {
    setEditingAccessorial(null)
    setCreateDrawerVisible(true)
  }

  const closeCreateDrawer = () => {
    setEditingAccessorial(null)
    setCreateDrawerVisible(false)
  }

  const handleCreateSuccess = () => {
    closeCreateDrawer()
    refetchAccessorials()
    message.success(
      editingAccessorial ? 'Manual accessorial updated successfully' : 'Manual accessorial created successfully'
    )
  }

  const showEditAccessorialDrawer = React.useCallback((accessorial: ManualAccessorial) => {
    setEditingAccessorial(accessorial)
    setCreateDrawerVisible(true)
  }, [])

  const showRateHistoryDrawer = React.useCallback((accessorial: ManualAccessorial) => {
    setSelectedAccessorialId(accessorial._id || accessorial.id || '')
    setRateHistoryDrawerVisible(true)
  }, [])

  const closeRateHistoryDrawer = () => {
    setRateHistoryDrawerVisible(false)
    setSelectedAccessorialId(null)
  }

  // Column definitions
  const columnDefs = React.useMemo(
    (): ColDef[] => [
      {
        headerName: 'Active',
        field: 'disabled',
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: ActiveRenderer,
        editable: false,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        filterValueGetter: (params) => (!params.data.disabled ? 'active' : 'inactive'),
        filterParams: {
          values: ['active', 'inactive'],
          valueFormatter: (params: ValueFormatterParams) => (params.value === 'active' ? 'Active' : 'Inactive'),
        },
      },
      {
        headerName: 'Name',
        field: 'name',
        minWidth: 150,
        editable: false,
        filter: 'agTextColumnFilter',
        enableRowGroup: true,
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
          debounceMs: 200,
        },
      },
      {
        headerName: 'Description',
        field: 'description',
        minWidth: 200,
        flex: 1,
        editable: false,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
          debounceMs: 200,
        },
      },
      {
        headerName: 'Region',
        field: 'freight_region_name',
        minWidth: 120,
        cellRenderer: ({ value }: { value: string }) =>
          value ? <BBDTag theme2>{value}</BBDTag> : <Texto appearance='hint'>All Regions</Texto>,
        editable: false,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
      },
      {
        headerName: 'Product Group',
        field: 'product_group',
        minWidth: 120,
        editable: false,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        cellRenderer: SingleProductGroupCellRenderer,
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => (params.value === null ? 'Any' : params.value),
          excelMode: 'windows',
          suppressSelectAll: false,
        },
      },
      {
        headerName: 'Fee Type',
        field: 'fee_type',
        minWidth: 150,
        editable: false,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        valueGetter: (params) => params.data.fee_type?.type || null,
        cellRenderer: (params: ICellRendererParams<ManualAccessorial>) => (
          <FeeTypeRenderer value={params.data?.fee_type} />
        ),
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => params.value || 'Any',
        },
      },
      {
        headerName: 'Rate',
        field: 'rate',
        minWidth: 100,
        cellRenderer: RateRenderer,
        editable: false,
        cellStyle: { textAlign: 'right' },
        headerClass: 'ag-right-aligned-header',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        filterValueGetter: (params) => {
          if (params.data.rate === null || params.data.rate === undefined) return null
          const feeType = params.data.fee_type?.type || ''
          const isPercentageType = feeType.includes('%')

          // For percentage types, return the percentage value for filtering
          return isPercentageType ? Number((params.data.rate * 100).toFixed(2)) : Number(params.data.rate)
        },
        filterParams: {
          defaultOption: 'equals',
          allowedCharPattern: '\\d\\-\\.',
        },
        valueSetter: (params) => {
          const { newValue } = params
          if (newValue !== params.oldValue) {
            const accessorialId = params.data._id || params.data.id
            createRateMutation.mutate({
              accessorial_id: accessorialId,
              effective_from_date: new Date().toISOString(),
              rate: newValue,
            })
          }
          return { ...params.data, rate: newValue }
        },
      },
      {
        headerName: 'Effective From',
        field: 'rates',
        minWidth: 120,
        cellRenderer: EffectiveDateRenderer,
        editable: false,
        filter: 'agDateColumnFilter',
      },
      {
        headerName: 'Effective To',
        field: 'rates',
        minWidth: 120,
        cellRenderer: ({ value }: { value: string }) => {
          if (!value || !Array.isArray(value) || value.length === 0) return <Texto appearance='hint'>N/A</Texto>

          // Get the most recent rate
          const sortedRates = [...value].sort(
            (a, b) => new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
          )
          const latestRate = sortedRates[0]

          return latestRate.effective_to ? (
            <Texto>{dayjs(latestRate.effective_to).format('MM/DD/YY')}</Texto>
          ) : (
            <Texto appearance='hint'>Indefinite</Texto>
          )
        },
        editable: false,
        filter: 'agDateColumnFilter',
      },
      {
        headerName: 'Next Rate',
        field: 'next_scheduled_rate',
        valueGetter: (params: ValueGetterParams<ManualAccessorial>) => {
          const date = params.data?.next_scheduled_rate?.effective_from
          return date ? new Date(date) : undefined
        },
        cellRenderer: NextRateRenderer,
        filter: 'agDateColumnFilter',
        filterParams: {
          browserDatePicker: true,
          minValidYear: 2000,
          maxValidYear: 2100,
          comparator: (filterDate: Date, cellValue: Date) => {
            if (!cellValue) return -1
            const filterDay = dayjs(filterDate).startOf('day')
            const cellDay = dayjs(cellValue).startOf('day')
            return cellDay.diff(filterDay, 'day')
          },
        },
      },
      {
        headerName: 'Approvals',
        field: 'requires_approval',
        maxWidth: 100,
        minWidth: 80,
        cellRenderer: ApprovalRenderer,
        filter: 'agSetColumnFilter',
        editable: false,
        enableRowGroup: true,
        filterParams: {
          values: [true, false],
          valueFormatter: (params: ValueFormatterParams) => (params.value ? 'REQ' : 'No'),
        },
      },
      {
        headerName: 'Reason Code',
        field: 'requires_reason_code',
        maxWidth: 120,
        minWidth: 100,
        cellRenderer: ReasonCodeRenderer,
        filter: 'agSetColumnFilter',
        editable: false,
        enableRowGroup: true,
        filterParams: {
          values: [true, false],
          valueFormatter: (params: ValueFormatterParams) => (params.value ? 'REQ' : 'No'),
        },
      },
      {
        headerName: 'Exclude Invoice',
        field: 'exclude_from_invoice',
        maxWidth: 120,
        minWidth: 100,
        cellRenderer: ExcludeFromInvoiceRenderer,
        filter: 'agSetColumnFilter',
        editable: false,
        enableRowGroup: true,
        filterParams: {
          values: [true, false],
          valueFormatter: (params: ValueFormatterParams) => (params.value ? 'REQ' : 'No'),
        },
      },
      {
        headerName: 'Actions',
        field: 'actions',
        maxWidth: 100,
        minWidth: 100,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<ManualAccessorial>) => {
          if (!params.data) return null
          return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              <Tooltip title='Edit'>
                <GraviButton
                  icon={<MaterialIcon size={16} className='pt-1' icon='edit' />}
                  onClick={(e) => {
                    e.stopPropagation()
                    showEditAccessorialDrawer(params.data as ManualAccessorial)
                  }}
                />
              </Tooltip>

              <Tooltip title='Rate Schedule'>
                <GraviButton
                  icon={<MaterialIcon size={16} className='pt-1' icon='overview' />}
                  onClick={(e) => {
                    e.stopPropagation()
                    showRateHistoryDrawer(params.data as ManualAccessorial)
                  }}
                />
              </Tooltip>
            </div>
          )
        },
      },
    ],
    [createRateMutation, showRateHistoryDrawer, showEditAccessorialDrawer]
  )

  return (
    <Horizontal style={{ margin: '0px -1em', height: '100%' }}>
      <Vertical flex={1}>
        <GraviGrid<ManualAccessorial, IFilter>
          key={`manual-accessorials-${records.length}`}
          rowData={records}
          columnDefs={columnDefs}
          loading={isLoadingAccessorials}
          storageKey='manual-accessorials-grid'
          controlBarProps={{
            title: 'Manual Accessorials',
            actionButtons: (
              <Horizontal gap={8}>
                <GraviButton
                  icon={<PlusCircleOutlined />}
                  onClick={showCreateDrawer}
                  buttonText='Create Manual Accessorial'
                  theme2
                />
              </Horizontal>
            ),
          }}
          agPropOverrides={{
            getRowId: (params) => params.data._id,
            rowSelection: 'multiple',
            rowGroupPanelShow: 'onlyWhenGrouping',
            enableRangeSelection: true,
          }}
        />

        <Drawer
          title={editingAccessorial ? 'Edit Manual Accessorial' : 'Create Manual Accessorial'}
          placement='right'
          styles={{ body: { padding: 0 } }}
          width={600}
          onClose={closeCreateDrawer}
          open={createDrawerVisible}
          destroyOnClose
        >
          <CreateAccessorialForm
            ratebook={ratebook}
            onCancel={closeCreateDrawer}
            onSuccess={handleCreateSuccess}
            editingAccessorial={editingAccessorial}
          />
        </Drawer>

        {rateHistoryDrawerVisible && selectedAccessorial && (
          <Drawer
            title={`Rate History - ${selectedAccessorial.name}`}
            placement='right'
            width={600}
            onClose={closeRateHistoryDrawer}
            open={rateHistoryDrawerVisible}
            destroyOnClose
          >
            <RateHistoryDrawer
              key={`rate-drawer-${selectedAccessorial._id || selectedAccessorial.id}-${
                selectedAccessorial.rates.length
              }`}
              accessorial={selectedAccessorial}
              onRateChange={async () => {
                // Refetch accessorials to update the data
                await refetchAccessorials()
              }}
            />
          </Drawer>
        )}
      </Vertical>
    </Horizontal>
  )
}
